import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import type { CategoriaActividadListDTO } from './categoria-actividad';

interface CategoriaActividadAutocompleteProps {
  value: string;
  initialLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  onChange: (actividadId: string, categoria?: CategoriaActividadListDTO) => void;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatCategoriaLabel(item: CategoriaActividadListDTO): string {
  const tipo = item.tipoActividad?.descripcion?.trim();
  if (tipo) return `${item.descripcion ?? `#${item.id}`} (${tipo})`;
  return item.descripcion ?? `#${item.id}`;
}

export default function CategoriaActividadAutocomplete({
  value,
  initialLabel,
  disabled,
  placeholder = 'Buscar actividad…',
  hasError,
  onChange,
}: CategoriaActividadAutocompleteProps) {
  const [items, setItems] = useState<CategoriaActividadListDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState(initialLabel ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => items.find(item => String(item.id) === String(value)),
    [items, value],
  );

  useEffect(() => {
    if (selected) {
      setInputText(formatCategoriaLabel(selected));
      return;
    }
    if (value && initialLabel) {
      setInputText(initialLabel);
      return;
    }
    if (!value) setInputText('');
  }, [value, selected, initialLabel]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetch('/api/categoria-actividads?size=99999&sort=descripcion,asc', {
      headers: { ...authHeader() },
    })
      .then(async res => {
        if (!res.ok) throw new Error(`Error ${res.status} al cargar actividades`);
        return res.json() as Promise<CategoriaActividadListDTO[]>;
      })
      .then(data => {
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'No se pudo cargar el catálogo.');
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        if (selected) {
          setInputText(formatCategoriaLabel(selected));
        } else if (value && initialLabel) {
          setInputText(initialLabel);
        } else if (!value) {
          setInputText('');
        }
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selected, value, initialLabel]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(item => {
      const descripcion = String(item.descripcion ?? '').toLowerCase();
      const tipo = String(item.tipoActividad?.descripcion ?? '').toLowerCase();
      return descripcion.includes(q) || tipo.includes(q);
    });
  }, [items, query]);

  const handleSelect = useCallback((item: CategoriaActividadListDTO) => {
    onChange(String(item.id), item);
    setInputText(formatCategoriaLabel(item));
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setInputText('');
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const inputBorder = hasError ? 'profase-form-input--error' : open ? 'profase-form-input--focused' : '';
  const inputClasses = ['profase-form-input', 'profase-form-input-with-clear', inputBorder]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className="categoria-actividad-autocomplete">
      <div className="profase-form-input-wrap">
        <input
          id="actividad-adicional-actividad"
          type="text"
          value={inputText}
          placeholder={loading ? 'Cargando actividades…' : placeholder}
          disabled={disabled || loading}
          onChange={e => {
            setQuery(e.target.value);
            setInputText(e.target.value);
            setOpen(true);
            if (value) onChange('');
          }}
          onFocus={() => {
            if (!disabled && !loading) {
              setOpen(true);
              setQuery('');
            }
          }}
          className={inputClasses}
          autoComplete="off"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            title="Limpiar"
            className="profase-form-clear-btn"
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {loadError && (
        <span className="actividad-adicional-modal__field-hint actividad-adicional-modal__field-hint--error">
          {loadError}
        </span>
      )}

      {open && !disabled && filtered.length > 0 && (
        <ul className="profase-form-dropdown">
          {filtered.map(item => (
            <li
              key={item.id}
              onMouseDown={() => handleSelect(item)}
              className={
                String(item.id) === String(value)
                  ? 'profase-form-dropdown-item profase-form-dropdown-item--selected'
                  : 'profase-form-dropdown-item'
              }
            >
              {formatCategoriaLabel(item)}
            </li>
          ))}
        </ul>
      )}

      {open && !disabled && !loading && filtered.length === 0 && query.length > 0 && (
        <div className="profase-form-dropdown-empty">Sin resultados</div>
      )}
    </div>
  );
}
